import React, { useState, useEffect, useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';
import { P, font, mono } from '../styles/theme';
import { Spinner, Card } from '../components/Shared';
import { CongTyService, KskImportService } from '../services/api';

/**
 * Import kết quả KSK từ Excel — web hóa frm_Import_KetQua_KSK (WinForms).
 * Luồng: chọn file → chọn SHEET → load data → chọn công ty → chọn/nhập số lô
 *        → Kiểm tra dữ liệu → Import.
 * Có thêm nhanh doanh nghiệp + số lô ngay trên trang (như cmdThemMoiKH/cmdthemsolo),
 * và xem cấu hình cột (cmdConfig → export_excel_struct_detail, report KETQUA_KSK).
 */

// Template cột cố định (index 1-based khớp file mẫu) — fallback khi chưa dùng config DB
const COLS = [null,
  'stt', 'barcode_xn', 'ma_nv', 'ho_ten', 'bo_phan', 'dien_thoai', 'ngay_sinh', 'gioi_tinh', 'ngay_kham',
  'theluc_chieucao', 'theluc_cannang', 'theluc_bmi', 'theluc_mach', 'theluc_huyetap',
  'noi_khoa', 'ngoaikhoa', 'sankhoa', 'khamlamsang_vu', 'mat', 'tmh_benh', 'rhm_benh', 'dalieu',
  'congthuc_mau_hongcau', 'congthuc_mau_bachcau', 'congthuc_mau_tieucau',
  'nuoctieu_duong', 'nuoctieu_protein',
  'viemgan_b', 'viemgan_a', 'viemgan_e', 'morphine',
  'ure', 'creatinin', 'ketluan_nhomthan', 'glucose', 'ketluan_nhomduong',
  'cholesterol', 'triglycerid', 'ldl', 'hdl', 'ketluan_nhommo',
  'acid_uric', 'ketluan_aciduric', 'alt_gpt', 'ast_got', 'ast_ggt', 'ketluan_nhomgan',
  'protein_toanphan', 'albumin', 'danh_gia',
  'pap_smear', 'CTC_AcidAcetic', 'soi_cotucung', 'nhom_mau', 'test_lao', 'cay_phan', 'xn_dom', 'soidich_amdao',
  'sieuam_tongquat', 'sieuam_tucung_phanphu', 'sieuam_tienliettuyen', 'sieuam_tuyengiap', 'sieuam_vu',
  'dien_tam_do', 'doloang_xuong', 'xquang_timphoi_thang',
  'phanloai_suckhoe', 'ketluan_suckhoe', 'tuvan_suckhoe',
];
const INFO_KEYS = new Set(['stt', 'ma_nv', 'ho_ten', 'bo_phan', 'dien_thoai', 'ngay_sinh', 'gioi_tinh', 'ngay_kham']);

// Nhãn hiển thị trên lưới
const LABELS = {
  stt: 'STT', barcode_xn: 'CODE', ma_nv: 'Mã NV', ho_ten: 'Họ và tên', bo_phan: 'Bộ phận',
  dien_thoai: 'SĐT', ngay_sinh: 'Ngày sinh', gioi_tinh: 'Giới tính', ngay_kham: 'Ngày khám',
  theluc_chieucao: 'Chiều cao', theluc_cannang: 'Cân nặng', theluc_bmi: 'BMI', theluc_mach: 'Mạch', theluc_huyetap: 'Huyết áp',
  noi_khoa: 'Nội khoa', ngoaikhoa: 'Ngoại khoa', sankhoa: 'Sản phụ khoa', khamlamsang_vu: 'LS vú',
  mat: 'Mắt', tmh_benh: 'TMH', rhm_benh: 'RHM', dalieu: 'Da liễu',
  congthuc_mau_hongcau: 'Hồng cầu', congthuc_mau_bachcau: 'Bạch cầu', congthuc_mau_tieucau: 'Tiểu cầu',
  nuoctieu_duong: 'Đường NT', nuoctieu_protein: 'Protein NT',
  viemgan_b: 'Viêm gan B', viemgan_a: 'Viêm gan A', viemgan_e: 'Viêm gan E', morphine: 'Morphine',
  ure: 'Ure', creatinin: 'Creatinin', ketluan_nhomthan: 'KL thận', glucose: 'Glucose', ketluan_nhomduong: 'KL đường',
  cholesterol: 'Cholesterol', triglycerid: 'Triglycerid', ldl: 'LDL', hdl: 'HDL', ketluan_nhommo: 'KL mỡ',
  acid_uric: 'Acid uric', ketluan_aciduric: 'KL acid uric', alt_gpt: 'ALT', ast_got: 'AST', ast_ggt: 'GGT', ketluan_nhomgan: 'KL gan',
  protein_toanphan: 'Protein TP', albumin: 'Albumin', danh_gia: 'Đánh giá',
  pap_smear: 'PAP smear', CTC_AcidAcetic: 'CTC AcidAcetic', soi_cotucung: 'Soi CTC', nhom_mau: 'Nhóm máu',
  test_lao: 'Test lao', cay_phan: 'Cấy phân', xn_dom: 'XN đờm', soidich_amdao: 'Soi dịch ÂĐ',
  sieuam_tongquat: 'SA ổ bụng', sieuam_tucung_phanphu: 'SA TC-PP', sieuam_tienliettuyen: 'SA TLT',
  sieuam_tuyengiap: 'SA giáp', sieuam_vu: 'SA vú', dien_tam_do: 'ĐTĐ', doloang_xuong: 'Loãng xương',
  xquang_timphoi_thang: 'XQ tim phổi', phanloai_suckhoe: 'Phân loại', ketluan_suckhoe: 'Kết luận', tuvan_suckhoe: 'Tư vấn',
};
// Các cột chi tiết hiển thị trên lưới (bỏ stt/barcode ở nhóm info đã có riêng)
const GRID_KEYS = COLS.filter(k => k && !INFO_KEYS.has(k));

// 'A' → 1, 'Z' → 26, 'AA' → 27...
const colLetterToIndex = (s) =>
  String(s || '').trim().toUpperCase().split('').reduce((a, ch) => a * 26 + (ch.charCodeAt(0) - 64), 0);

const fmtCell = (v) => {
  if (v == null) return '';
  if (v instanceof Date) {
    const dd = String(v.getDate()).padStart(2, '0');
    const mm = String(v.getMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}/${v.getFullYear()}`;
  }
  return String(v).trim();
};

export default function AdminImportKskPage({ onBack }) {
  // File & sheet
  const [workbook, setWorkbook] = useState(null);
  const [sheetNames, setSheetNames] = useState([]);
  const [sheet, setSheet] = useState('');
  const [fileName, setFileName] = useState('');
  const [fileObj, setFileObj] = useState(null); // File gốc — phục vụ nút "Mở file Excel"

  // Công ty & số lô
  const [companies, setCompanies] = useState([]);
  const [company, setCompany] = useState(null);
  const [searchCty, setSearchCty] = useState('');
  const [openCty, setOpenCty] = useState(false);
  const [soLoList, setSoLoList] = useState([]);
  const [soLo, setSoLo] = useState('');
  const [openSoLo, setOpenSoLo] = useState(false);
  const [ngayKham, setNgayKham] = useState(() => new Date().toISOString().slice(0, 10));

  // Modals
  const [showAddCty, setShowAddCty] = useState(false);
  const [showAddSoLo, setShowAddSoLo] = useState(false);
  const [showConfig, setShowConfig] = useState(false);

  // Cấu hình cột từ DB (export_excel_struct) — null = chưa load / không có → dùng template cứng
  const [colConfig, setColConfig] = useState(null);
  const loadColConfig = async () => {
    try {
      const cfg = await KskImportService.getColumns();
      setColConfig(cfg && cfg.details && cfg.details.length > 0 ? cfg : null);
      return cfg;
    } catch { setColConfig(null); return null; }
  };
  useEffect(() => { loadColConfig(); }, []);

  // Data
  const [rows, setRows] = useState([]);
  const [checked, setChecked] = useState(new Set());
  // Bộ lọc danh sách đã load
  const [flt, setFlt] = useState({ maNv: '', hoTen: '', sdt: '', gioiTinh: '', code: '' });
  const [validated, setValidated] = useState(false);
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState(null);
  const [summary, setSummary] = useState(null);
  const ctyRef = useRef(null);
  const soLoRef = useRef(null);
  const fileRef = useRef(null);

  const loadCompanies = async () => {
    try { setCompanies(await CongTyService.list() || []); }
    catch { setMessage({ type: 'err', text: 'Không tải được danh mục công ty (cần quyền admin).' }); }
  };
  useEffect(() => { loadCompanies(); }, []);

  // Số lô theo công ty
  const loadSoLo = async (idKhachHang) => {
    try { setSoLoList(await KskImportService.getSoLo(idKhachHang) || []); }
    catch { setSoLoList([]); }
  };
  useEffect(() => {
    if (company?.idKhachHang) loadSoLo(company.idKhachHang);
    else setSoLoList([]);
  }, [company]);

  useEffect(() => {
    const h = (e) => {
      if (ctyRef.current && !ctyRef.current.contains(e.target)) setOpenCty(false);
      if (soLoRef.current && !soLoRef.current.contains(e.target)) setOpenSoLo(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const filteredCty = useMemo(() => {
    const q = searchCty.trim().toLowerCase();
    if (!q) return companies;
    return companies.filter(c =>
      (c.tenKhachHang || '').toLowerCase().includes(q) ||
      (c.maKhachHang || '').toLowerCase().includes(q) ||
      (c.maSoThue || '').toLowerCase().includes(q));
  }, [companies, searchCty]);

  const filteredSoLo = useMemo(() => {
    const q = soLo.trim().toLowerCase();
    if (!q) return soLoList;
    return soLoList.filter(s =>
      String(s.ma_solo || '').toLowerCase().includes(q) ||
      String(s.ten_solo || '').toLowerCase().includes(q));
  }, [soLoList, soLo]);

  // ---------- B1: chọn file → liệt kê sheet ----------
  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy('parse'); setMessage(null); setValidated(false); setSummary(null); setRows([]);
    try {
      const wb = XLSX.read(await f.arrayBuffer(), { cellDates: true });
      setWorkbook(wb);
      setSheetNames(wb.SheetNames);
      setFileName(f.name);
      setFileObj(f);
      // Ưu tiên sheet "CÁ NHÂN" nếu có
      const def = wb.SheetNames.find(n => n.trim().toUpperCase() === 'CÁ NHÂN') || '';
      setSheet(def);
      if (def) parseSheet(wb, def);
      else setMessage({ type: 'warn', text: `Đã đọc file (${wb.SheetNames.length} sheet). Chọn sheet chứa dữ liệu để load.` });
    } catch (err) {
      setWorkbook(null); setSheetNames([]); setFileName(''); setFileObj(null);
      setMessage({ type: 'err', text: 'Lỗi đọc file: ' + err.message });
    } finally {
      setBusy('');
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  // ---------- B2: parse sheet được chọn ----------
  // Ưu tiên cấu hình cột từ DB (excel_column → sql_column, data từ row_begin);
  // chưa có cấu hình → fallback template cứng theo file mẫu (dòng đánh số 1,2,3...)
  const parseSheet = (wb, name, cfgOverride) => {
    const cfg = cfgOverride !== undefined ? cfgOverride : colConfig;
    setBusy('parse'); setMessage(null); setValidated(false); setSummary(null);
    try {
      const ws = wb.Sheets[name];
      if (!ws) throw new Error(`Không đọc được sheet "${name}"`);
      const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null });

      const parsed = [];
      const pushRow = (map, sttFallback) => {
        const hoTen = map.ho_ten || '';
        const maNv = map.ma_nv || '';
        if (!hoTen && !maNv) return;
        const chiTieu = {};
        Object.entries(map).forEach(([k, v]) => {
          if (!INFO_KEYS.has(k) && v) chiTieu[k] = v;
        });
        parsed.push({
          stt: parseInt(map.stt, 10) || sttFallback,
          maNv, hoTen,
          boPhan: map.bo_phan || '',
          dienThoai: map.dien_thoai || '',
          soCccd: map.so_cccd || map.so_cancuoc || '',
          diaChi: map.dia_chi || '',
          gioiTinh: map.gioi_tinh || '',
          ngaySinh: map.ngay_sinh || '',
          ngayKham: map.ngay_kham || '',
          chiTieu,
          idBenhNhan: 0, maLuotKham: '', errors: [], importStatus: '',
        });
      };

      if (cfg && cfg.details && cfg.details.length > 0) {
        // ==== Theo cấu hình DB ====
        const startIdx = Math.max((cfg.rowBegin || 1) - 1, 0);
        for (let i = startIdx; i < aoa.length; i++) {
          const r = aoa[i] || [];
          const map = {};
          cfg.details.forEach(d => {
            const idx = colLetterToIndex(d.excelColumn);
            if (idx > 0) {
              const val = fmtCell(r[idx - 1]);
              if (val) map[d.sqlColumn] = val;
            }
          });
          pushRow(map, parsed.length + 1);
        }
        if (parsed.length === 0)
          throw new Error(`Sheet "${name}" không có dữ liệu từ dòng ${cfg.rowBegin} (theo cấu hình cột)`);
      } else {
        // ==== Fallback template cứng ====
        let numberRow = -1;
        for (let i = 0; i < Math.min(aoa.length, 20); i++) {
          const r = aoa[i] || [];
          if (String(r[0]).trim() === '1' && String(r[1]).trim() === '2' && String(r[2]).trim() === '3') { numberRow = i; break; }
        }
        if (numberRow < 0) throw new Error(`Sheet "${name}" không đúng template (không thấy dòng đánh số cột 1,2,3...)`);
        for (let i = numberRow + 1; i < aoa.length; i++) {
          const r = aoa[i] || [];
          const map = {};
          for (let c = 1; c < COLS.length; c++) {
            const key = COLS[c];
            if (!key) continue;
            const val = fmtCell(r[c - 1]);
            if (val) map[key] = val;
          }
          pushRow(map, parsed.length + 1);
        }
        if (parsed.length === 0) throw new Error(`Sheet "${name}" không có dòng dữ liệu`);
      }

      setRows(parsed);
      setChecked(new Set(parsed.map((_, i) => i)));
      setMessage({
        type: 'ok',
        text: `Đã load ${parsed.length} dòng từ sheet "${name}"${cfg && cfg.details?.length ? ' (theo cấu hình cột DB)' : ' (theo template mặc định)'}.`,
      });
    } catch (err) {
      setRows([]);
      setMessage({ type: 'err', text: err.message });
    } finally { setBusy(''); }
  };

  // ---------- Validate / Import ----------
  const buildPayload = () => ({
    soLo: soLo.trim(),
    idKhachHang: company?.idKhachHang || 0,
    ngayKhamChung: ngayKham ? ngayKham.split('-').reverse().join('/') : '',
    rows: rows.filter((_, i) => checked.has(i)),
  });

  const preCheck = () => {
    if (rows.length === 0) { setMessage({ type: 'err', text: 'Chưa có dữ liệu — chọn file và sheet trước.' }); return false; }
    if (!company) { setMessage({ type: 'err', text: 'Bạn cần chọn công ty/doanh nghiệp.' }); return false; }
    if (!soLo.trim()) { setMessage({ type: 'err', text: 'Bạn cần chọn hoặc nhập số lô.' }); return false; }
    if (checked.size === 0) { setMessage({ type: 'err', text: 'Bạn cần tích chọn các dòng cần xử lý.' }); return false; }
    return true;
  };

  const doValidate = async () => {
    if (!preCheck()) return;
    setBusy('validate'); setMessage(null); setSummary(null);
    try {
      const chk = await KskImportService.checkSoLo(soLo.trim());
      if (chk.exists && !window.confirm(`Số lô "${soLo.trim()}" đã tồn tại trong hệ thống.\nBạn có muốn thêm danh sách này vào lô tiếp không?`)) {
        setBusy(''); return;
      }
      const res = await KskImportService.validate(buildPayload());
      applyResult(res);
      setValidated(!res.hasError);
      setMessage(res.hasError
        ? { type: 'err', text: 'Có lỗi dữ liệu — xem cột "Lỗi" trên lưới.' }
        : { type: 'ok', text: `Dữ liệu hợp lệ. ${res.rows.filter(r => r.maLuotKham).length} dòng đã tồn tại trong lô (sẽ bỏ qua khi import).` });
    } catch (err) {
      setMessage({ type: 'err', text: err.response?.data?.message || 'Lỗi kiểm tra dữ liệu.' });
    } finally { setBusy(''); }
  };

  const doImport = async () => {
    if (!preCheck()) return;
    if (!validated) { setMessage({ type: 'err', text: 'Bấm "Kiểm tra dữ liệu" thành công trước khi import.' }); return; }
    if (!window.confirm(`Import ${checked.size} dòng vào hệ thống HIS?`)) return;
    setBusy('import'); setMessage(null);
    try {
      const res = await KskImportService.commit(buildPayload());
      applyResult(res);
      setSummary(res);
      setValidated(false);
      if (company?.idKhachHang) loadSoLo(company.idKhachHang);
      setMessage(res.failed > 0
        ? { type: 'warn', text: `Hoàn tất: ${res.success} thành công, ${res.skipped} bỏ qua, ${res.failed} lỗi.` }
        : { type: 'ok', text: `Hoàn tất: ${res.success} thành công, ${res.skipped} bỏ qua (đã tồn tại).` });
    } catch (err) {
      setMessage({ type: 'err', text: err.response?.data?.message || 'Import thất bại.' });
    } finally { setBusy(''); }
  };

  const applyResult = (res) => {
    const sent = [...checked].sort((a, b) => a - b);
    const next = [...rows];
    res.rows.forEach((r, j) => {
      const idx = sent[j];
      if (idx != null) next[idx] = { ...next[idx], ...r };
    });
    setRows(next);
  };

  // Mở lại file Excel gốc (tương đương mở txtSourceFile bên WinForms).
  // Browser không cho web app lấy đường dẫn thật/ mở file theo path — nên mở
  // từ chính bản file đang giữ trong bộ nhớ (tải xuống → Excel mở).
  const openExcelFile = () => {
    if (!fileObj) return;
    const url = URL.createObjectURL(fileObj);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileObj.name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  // Xuất file kết quả import (Result_{file}_{timestamp}.xlsx) — web hóa phần
  // lưu file kết quả cuối cmdImportToHis_Click
  const exportResult = () => {
    const statusText = (r) =>
      r.importStatus === 'ok' ? 'Thành công'
        : r.importStatus === 'skip' ? 'Đã tồn tại'
          : r.importStatus === 'error' ? 'Không thành công' : '';
    const keys = gridKeys;
    const header = ['Trạng thái Import', 'Lỗi', 'Mã lượt khám', 'ID Bệnh nhân', 'STT', 'Mã NV', 'Họ tên', 'Bộ phận', 'SĐT', 'Ngày sinh', 'Giới tính', 'Ngày khám',
      ...keys.map(k => labelOf(k))];
    const data = rows.map(r => [
      statusText(r), (r.errors || []).join('; '), r.maLuotKham, r.idBenhNhan || '',
      r.stt, r.maNv, r.hoTen, r.boPhan, r.dienThoai, r.ngaySinh, r.gioiTinh, r.ngayKham,
      ...keys.map(k => r.chiTieu?.[k] || ''),
    ]);
    const ws = XLSX.utils.aoa_to_sheet([header, ...data]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'KetQuaImport');
    const ts = new Date().toISOString().replace(/[-:T]/g, '').slice(0, 14);
    const base = (fileName || 'import').replace(/\.(xlsx?|xls)$/i, '');
    XLSX.writeFile(wb, `Result_${base}_${ts}.xlsx`);
  };

  // Danh sách hiển thị sau lọc — GIỮ index gốc để checkbox/validate/import không lệch dòng
  const visibleRows = useMemo(() => {
    const q = (s) => (s || '').toString().toLowerCase();
    const fMa = q(flt.maNv), fTen = q(flt.hoTen), fSdt = q(flt.sdt), fCode = q(flt.code);
    return rows
      .map((r, i) => ({ r, i }))
      .filter(({ r }) =>
        (!fMa || q(r.maNv).includes(fMa)) &&
        (!fTen || q(r.hoTen).includes(fTen)) &&
        (!fSdt || q(r.dienThoai).includes(fSdt)) &&
        (!flt.gioiTinh || q(r.gioiTinh) === q(flt.gioiTinh)) &&
        (!fCode || q(r.chiTieu?.barcode_xn).includes(fCode)));
  }, [rows, flt]);
  const isFiltered = visibleRows.length !== rows.length;

  const allVisibleChecked = visibleRows.length > 0 && visibleRows.every(({ i }) => checked.has(i));
  const toggleAll = () => {
    setChecked(prev => {
      const s = new Set(prev);
      if (allVisibleChecked) visibleRows.forEach(({ i }) => s.delete(i));
      else visibleRows.forEach(({ i }) => s.add(i));
      return s;
    });
    setValidated(false);
  };
  const toggleOne = (i) => { setChecked(p => { const s = new Set(p); s.has(i) ? s.delete(i) : s.add(i); return s; }); setValidated(false); };

  const inputStyle = {
    width: '100%', boxSizing: 'border-box', padding: '10px 12px',
    borderRadius: 8, border: '1px solid ' + P.border, background: P.inputBg,
    fontSize: 14, fontFamily: font, color: P.text, outline: 'none',
  };
  const lbl = { display: 'block', fontSize: 13, fontWeight: 600, color: P.textDim, marginBottom: 6 };
  const th = { padding: '8px 10px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: P.textDim, whiteSpace: 'nowrap', borderBottom: '2px solid ' + P.border, position: 'sticky', top: 0, background: P.inputBg, zIndex: 2 };
  const td = { padding: '7px 10px', fontSize: 13, color: P.text, borderBottom: '1px solid ' + P.border, whiteSpace: 'nowrap' };
  const plusBtn = {
    width: 41, height: 41, borderRadius: 8, flexShrink: 0,
    border: '1px solid ' + P.primary + '50', background: P.primaryLight, color: P.primary,
    fontSize: 20, fontWeight: 700, cursor: 'pointer',
  };

  // Cột hiển thị trên lưới: theo config DB nếu có, ngược lại template cứng
  const gridKeys = useMemo(() => {
    if (colConfig?.details?.length)
      return colConfig.details.map(d => d.sqlColumn).filter(k => k && !INFO_KEYS.has(k));
    return GRID_KEYS;
  }, [colConfig]);
  const labelOf = (k) => {
    const d = colConfig?.details?.find(x => x.sqlColumn === k);
    return (d?.sqlColumnDescription || '').trim() || LABELS[k] || k;
  };

  const statusBadge = (r) => {
    if (r.importStatus === 'ok') return <Bdg bg="#e8f8ef" c={P.success} t="Thành công" />;
    if (r.importStatus === 'skip') return <Bdg bg="#eef2f7" c={P.textDim} t="Đã tồn tại" />;
    if (r.importStatus === 'error') return <Bdg bg="#fde8e8" c={P.danger} t="Lỗi" />;
    if (r.maLuotKham) return <Bdg bg="#eef2f7" c={P.textDim} t="Có trong lô" />;
    return null;
  };

  return (
    <div style={{ minHeight: '100vh', background: P.bg, fontFamily: font, color: P.text }}>
      <header className="app-header" style={{
        position: 'sticky', top: 0, zIndex: 10,
        background: P.headerBg, borderBottom: '2px solid ' + P.primaryDark,
        padding: '12px 24px', display: 'flex', alignItems: 'center', gap: 12,
        boxShadow: '0 2px 8px rgba(0,114,188,0.15)',
      }}>
        <button onClick={onBack} title="Quay lại" style={{
          width: 36, height: 36, borderRadius: 8, background: 'rgba(255,255,255,0.15)',
          border: '1px solid rgba(255,255,255,0.3)', color: P.headerText, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
          </svg>
        </button>
        <div style={{ flex: 1 }}>
          <div className="header-title" style={{ fontSize: 15, fontWeight: 700, color: P.headerText }}>PHÒNG KHÁM ĐA KHOA KCN QUẾ VÕ</div>
          <div className="header-sub" style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)' }}>Import kết quả khám sức khỏe từ Excel</div>
        </div>
        <button onClick={() => setShowConfig(true)} title="Cấu hình cột import (export_excel_struct)" style={{
          padding: '8px 14px', borderRadius: 8, background: 'rgba(255,255,255,0.15)',
          border: '1px solid rgba(255,255,255,0.3)', color: P.headerText, cursor: 'pointer',
          fontFamily: font, fontSize: 13, fontWeight: 600,
        }}>⚙ Cấu hình cột</button>
      </header>

      <div className="page-content" style={{ maxWidth: 1500, margin: '0 auto', padding: '24px 20px 60px' }}>

        {/* ===== B1: File + Sheet ===== */}
        <Card style={{ padding: '18px 24px', marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <span style={stepBadge}>1</span>
            <span style={{ fontSize: 14.5, fontWeight: 700, color: P.primary }}>Chọn file Excel &amp; sheet dữ liệu</span>
          </div>
          <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleFile} />
          <div className="import-step" style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            {/* File nguồn: [đường dẫn][Chọn file][Mở] gộp 1 cụm */}
            <div style={{ flex: '2 1 360px' }}>
              <label style={lbl}>File nguồn</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input style={{ ...inputStyle, fontFamily: mono, background: '#eef2f7', color: fileName ? P.text : P.textMuted }}
                  readOnly value={fileName} placeholder="Chưa chọn file..."
                  title={fileName ? `${fileName} — trình duyệt không cho web đọc đường dẫn đầy đủ trên máy` : ''}
                  onClick={() => !fileName && fileRef.current?.click()} />
                <button onClick={() => fileRef.current?.click()} disabled={busy !== ''}
                  style={{
                    padding: '10px 18px', borderRadius: 8, height: 41, whiteSpace: 'nowrap', flexShrink: 0,
                    border: 'none', background: P.primary, color: '#fff',
                    fontSize: 14, fontWeight: 600, fontFamily: font, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}>
                  {busy === 'parse' ? <Spinner size={16} color="#fff" /> : '📂'} Chọn file
                </button>
                <button onClick={openExcelFile} disabled={!fileObj} title="Mở file Excel này"
                  style={{
                    width: 41, height: 41, borderRadius: 8, flexShrink: 0, fontSize: 17,
                    border: '1px solid ' + P.primary + '50',
                    background: fileObj ? P.primaryLight : '#eef2f7',
                    color: fileObj ? P.primary : P.textMuted,
                    cursor: fileObj ? 'pointer' : 'not-allowed',
                  }}>📄</button>
              </div>
            </div>
            <div style={{ flex: '1 1 200px', maxWidth: 300 }}>
              <label style={lbl}>Sheet dữ liệu</label>
              <select style={{ ...inputStyle, opacity: workbook ? 1 : 0.6 }} value={sheet} disabled={!workbook}
                onChange={e => { setSheet(e.target.value); if (e.target.value) parseSheet(workbook, e.target.value); }}>
                <option value="">{workbook ? '-- Chọn sheet --' : 'Chọn file trước'}</option>
                {sheetNames.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          </div>
        </Card>

        {/* ===== B2: Công ty + Số lô + Ngày khám + Actions ===== */}
        <Card style={{ padding: '18px 24px', marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <span style={stepBadge}>2</span>
            <span style={{ fontSize: 14.5, fontWeight: 700, color: P.primary }}>Thông tin đợt import &amp; thực hiện</span>
          </div>
          <div className="import-step" style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            {/* Công ty + nút thêm */}
            <div style={{ flex: '2 1 300px' }}>
              <label style={lbl}>Công ty / doanh nghiệp <span style={{ color: P.danger }}>*</span></label>
              <div style={{ display: 'flex', gap: 8 }}>
                <div ref={ctyRef} style={{ position: 'relative', flex: 1 }}>
                  <input style={inputStyle} placeholder="Gõ tên, mã hoặc MST..."
                    value={searchCty}
                    onChange={e => { setSearchCty(e.target.value); setOpenCty(true); if (company) { setCompany(null); setSoLo(''); } }}
                    onFocus={() => setOpenCty(true)} />
                  {openCty && (
                    <div style={dropStyle}>
                      {filteredCty.map(c => (
                        <div key={c.idKhachHang}
                          onClick={() => { setCompany(c); setSearchCty(c.tenKhachHang); setOpenCty(false); setSoLo(''); }}
                          style={dropItem}
                          onMouseEnter={e => e.currentTarget.style.background = P.primaryLight}
                          onMouseLeave={e => e.currentTarget.style.background = P.white}>
                          <div style={{ fontSize: 14, fontWeight: 600 }}>{c.tenKhachHang}</div>
                          <div style={{ fontSize: 12, color: P.textMuted, fontFamily: mono }}>{c.maKhachHang}{c.maSoThue ? ' • MST: ' + c.maSoThue : ''}</div>
                        </div>
                      ))}
                      {filteredCty.length === 0 && <div style={{ padding: '10px 14px', fontSize: 13, color: P.textMuted }}>Không tìm thấy</div>}
                    </div>
                  )}
                </div>
                <button style={plusBtn} title="Thêm doanh nghiệp mới" onClick={() => setShowAddCty(true)}>＋</button>
              </div>
            </div>

            {/* Số lô: combobox theo công ty + nhập tự do + nút thêm */}
            <div style={{ flex: '1 1 220px' }}>
              <label style={lbl}>Số lô <span style={{ color: P.danger }}>*</span></label>
              <div style={{ display: 'flex', gap: 8 }}>
                <div ref={soLoRef} style={{ position: 'relative', flex: 1 }}>
                  <input style={inputStyle}
                    placeholder={company ? 'Chọn lô có sẵn hoặc nhập lô mới' : 'Chọn công ty trước'}
                    value={soLo} disabled={!company}
                    onChange={e => { setSoLo(e.target.value); setOpenSoLo(true); setValidated(false); }}
                    onFocus={() => setOpenSoLo(true)} />
                  {openSoLo && company && filteredSoLo.length > 0 && (
                    <div style={dropStyle}>
                      {filteredSoLo.map(s => (
                        <div key={s.id}
                          onClick={() => { setSoLo(String(s.ma_solo || '')); setOpenSoLo(false); setValidated(false); }}
                          style={dropItem}
                          onMouseEnter={e => e.currentTarget.style.background = P.primaryLight}
                          onMouseLeave={e => e.currentTarget.style.background = P.white}>
                          <span style={{ fontFamily: mono, fontWeight: 600 }}>{s.ma_solo}</span>
                          <span style={{ fontSize: 12, color: P.textMuted, marginLeft: 8 }}>{s.ten_solo}{s.so_luong ? ` • SL: ${s.so_luong}` : ''}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <button style={plusBtn} title="Thêm số lô mới" disabled={!company} onClick={() => setShowAddSoLo(true)}>＋</button>
              </div>
            </div>

            <div style={{ flex: '0 1 160px' }}>
              <label style={lbl}>Ngày khám (mặc định)</label>
              <input type="date" style={inputStyle} value={ngayKham} onChange={e => setNgayKham(e.target.value)} />
            </div>

            <button onClick={doValidate} disabled={busy !== '' || rows.length === 0}
              style={btnStyle(rows.length > 0 && busy === '', P.warning)}>
              {busy === 'validate' ? <Spinner size={16} color={P.white} /> : '🔍'} Kiểm tra dữ liệu
            </button>
            <button onClick={doImport} disabled={busy !== '' || !validated}
              style={btnStyle(validated && busy === '', P.primary)}>
              {busy === 'import' ? <Spinner size={16} color={P.white} /> : '⬆️'} Import vào HIS
            </button>
          </div>
        </Card>

        {message && (
          <div style={{
            padding: '12px 18px', borderRadius: 10, marginBottom: 14, fontSize: 14, fontWeight: 500,
            background: message.type === 'ok' ? '#e8f8ef' : message.type === 'warn' ? '#fdf3e7' : '#fde8e8',
            color: message.type === 'ok' ? P.success : message.type === 'warn' ? P.warning : P.danger,
            border: '1px solid ' + (message.type === 'ok' ? P.success : message.type === 'warn' ? P.warning : P.danger) + '40',
          }}>
            {message.text}
            {rows.length > 0 && (
              <span style={{ fontWeight: 400 }}>
                {' '}— đã chọn {checked.size}/{rows.length} dòng{isFiltered ? ` • đang lọc: ${visibleRows.length} dòng` : ''}
              </span>
            )}

            {/* Bộ lọc — ghép chung trong panel thông báo */}
            {rows.length > 0 && (
              <div className="import-filter" style={{
                display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center',
                marginTop: 10, paddingTop: 10,
                borderTop: '1px dashed ' + (message.type === 'ok' ? P.success : message.type === 'warn' ? P.warning : P.danger) + '50',
              }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: P.textDim }}>🔎 Lọc:</span>
                <input style={{ ...inputStyle, width: 140 }} placeholder="Mã nhân viên"
                  value={flt.maNv} onChange={e => setFlt({ ...flt, maNv: e.target.value })} />
                <input style={{ ...inputStyle, width: 180 }} placeholder="Họ và tên"
                  value={flt.hoTen} onChange={e => setFlt({ ...flt, hoTen: e.target.value })} />
                <input style={{ ...inputStyle, width: 140 }} placeholder="Điện thoại"
                  value={flt.sdt} onChange={e => setFlt({ ...flt, sdt: e.target.value })} />
                <select style={{ ...inputStyle, width: 120 }} value={flt.gioiTinh}
                  onChange={e => setFlt({ ...flt, gioiTinh: e.target.value })}>
                  <option value="">Giới tính</option>
                  <option value="Nam">Nam</option>
                  <option value="Nữ">Nữ</option>
                </select>
                <input style={{ ...inputStyle, width: 130 }} placeholder="Code XN"
                  value={flt.code} onChange={e => setFlt({ ...flt, code: e.target.value })} />
                {isFiltered && (
                  <button onClick={() => setFlt({ maNv: '', hoTen: '', sdt: '', gioiTinh: '', code: '' })}
                    style={{
                      padding: '8px 14px', borderRadius: 8, border: '1px solid ' + P.border,
                      background: P.white, color: P.textDim, fontSize: 13, fontFamily: font, cursor: 'pointer',
                    }}>✕ Xóa lọc</button>
                )}
              </div>
            )}
          </div>
        )}

        {summary && (
          <Card style={{ padding: '14px 20px', marginBottom: 14, display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 14, alignItems: 'center' }}>
            <span>Tổng gửi: <strong>{summary.total}</strong></span>
            <span style={{ color: P.success }}>Thành công: <strong>{summary.success}</strong></span>
            <span style={{ color: P.textDim }}>Bỏ qua: <strong>{summary.skipped}</strong></span>
            <span style={{ color: P.danger }}>Lỗi: <strong>{summary.failed}</strong></span>
            {/* Xuất file kết quả — như phần lưu Result_*.xls của cmdImportToHis_Click */}
            <button onClick={exportResult} style={{
              marginLeft: 'auto', padding: '8px 16px', borderRadius: 8,
              border: '1px solid ' + P.primary + '50', background: P.white, color: P.primary,
              fontSize: 13, fontWeight: 600, fontFamily: font, cursor: 'pointer',
            }}>⬇ Tải file kết quả</button>
          </Card>
        )}

        {/* ===== Lưới: hiển thị TOÀN BỘ cột ===== */}
        {rows.length > 0 && (
          <Card style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ overflow: 'auto', maxHeight: '60vh' }}>
              <table style={{ borderCollapse: 'collapse', minWidth: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ ...th, zIndex: 3 }}>
                      <input type="checkbox" checked={allVisibleChecked} onChange={toggleAll}
                        title={isFiltered ? 'Chọn/bỏ chọn các dòng ĐANG HIỂN THỊ' : 'Chọn/bỏ chọn tất cả'} />
                    </th>
                    <th style={th}>Trạng thái</th>
                    <th style={{ ...th, minWidth: 200, color: P.danger }}>Lỗi</th>
                    <th style={th}>Mã lượt khám</th>
                    <th style={th}>{LABELS.stt}</th>
                    <th style={th}>{LABELS.ma_nv}</th>
                    <th style={{ ...th, minWidth: 150 }}>{LABELS.ho_ten}</th>
                    <th style={th}>{LABELS.bo_phan}</th>
                    <th style={th}>{LABELS.dien_thoai}</th>
                    <th style={th}>{LABELS.ngay_sinh}</th>
                    <th style={th}>{LABELS.gioi_tinh}</th>
                    <th style={th}>{LABELS.ngay_kham}</th>
                    {gridKeys.map(k => <th key={k} style={th}>{labelOf(k)}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map(({ r, i }) => {
                    const hasErr = (r.errors || []).length > 0;
                    return (
                      <tr key={i} style={{ background: hasErr ? '#fff5f5' : r.importStatus === 'ok' ? '#f2fcf6' : 'transparent' }}>
                        <td style={td}><input type="checkbox" checked={checked.has(i)} onChange={() => toggleOne(i)} /></td>
                        <td style={td}>{statusBadge(r)}</td>
                        <td style={{ ...td, whiteSpace: 'normal', minWidth: 200, color: P.danger, fontSize: 12 }}>{(r.errors || []).join('; ')}</td>
                        <td style={{ ...td, fontFamily: mono, color: P.primary, fontWeight: 600 }}>{r.maLuotKham}</td>
                        <td style={{ ...td, fontFamily: mono, color: P.textMuted }}>{r.stt}</td>
                        <td style={{ ...td, fontFamily: mono }}>{r.maNv}</td>
                        <td style={{ ...td, fontWeight: 600 }}>{r.hoTen}</td>
                        <td style={td}>{r.boPhan}</td>
                        <td style={{ ...td, fontFamily: mono }}>{r.dienThoai}</td>
                        <td style={{ ...td, fontFamily: mono }}>{r.ngaySinh}</td>
                        <td style={td}>{r.gioiTinh}</td>
                        <td style={{ ...td, fontFamily: mono }}>{r.ngayKham}</td>
                        {gridKeys.map(k => (
                          <td key={k} style={{ ...td, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.chiTieu?.[k] || ''}>
                            {r.chiTieu?.[k] || ''}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {rows.length === 0 && (
          <Card style={{ padding: '56px 24px', textAlign: 'center' }}>
            <div style={{ fontSize: 44, marginBottom: 12 }}>📥</div>
            <p style={{ color: P.textDim, fontSize: 15, margin: 0 }}>
              Chọn file Excel → chọn sheet dữ liệu → chọn công ty → chọn/nhập số lô.
            </p>
          </Card>
        )}
      </div>

      {/* ===== Modals ===== */}
      {showAddCty && (
        <AddCompanyModal
          onClose={() => setShowAddCty(false)}
          onSaved={async (c) => {
            setShowAddCty(false);
            await loadCompanies();
            setCompany(c); setSearchCty(c.tenKhachHang);
          }} />
      )}
      {showAddSoLo && company && (
        <AddSoLoModal company={company}
          onClose={() => setShowAddSoLo(false)}
          onSaved={async (ma) => {
            setShowAddSoLo(false);
            await loadSoLo(company.idKhachHang);
            setSoLo(ma); setValidated(false);
          }} />
      )}
      {showConfig && (
        <ColumnConfigModal
          onClose={() => setShowConfig(false)}
          onChanged={async () => {
            const cfg = await loadColConfig();
            const usable = cfg && cfg.details && cfg.details.length > 0 ? cfg : null;
            if (workbook && sheet) parseSheet(workbook, sheet, usable);
          }} />
      )}
    </div>
  );
}

/* ============ Modal thêm doanh nghiệp (cmdThemMoiKH) ============ */
function AddCompanyModal({ onClose, onSaved }) {
  const [ma, setMa] = useState('');
  const [ten, setTen] = useState('');
  const [mst, setMst] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const save = async () => {
    if (!ten.trim()) { setErr('Tên doanh nghiệp không được trống'); return; }
    setSaving(true); setErr('');
    try {
      const res = await KskImportService.addKhachHang(ma.trim(), ten.trim(), mst.trim());
      onSaved({ idKhachHang: res.id, maKhachHang: res.ma, tenKhachHang: ten.trim(), maSoThue: mst.trim() });
    } catch (e) {
      setErr(e.response?.data?.message || 'Không lưu được doanh nghiệp');
      setSaving(false);
    }
  };

  return (
    <Modal title="Thêm doanh nghiệp mới" onClose={onClose}>
      <Field label="Mã (bỏ trống = tự sinh)"><input style={mInput} value={ma} onChange={e => setMa(e.target.value)} /></Field>
      <Field label="Tên doanh nghiệp *"><input style={mInput} value={ten} onChange={e => setTen(e.target.value)} autoFocus /></Field>
      <Field label="Mã số thuế"><input style={mInput} value={mst} onChange={e => setMst(e.target.value)} /></Field>
      {err && <div style={{ color: P.danger, fontSize: 13, marginTop: 8 }}>{err}</div>}
      <ModalActions onClose={onClose} onSave={save} saving={saving} />
    </Modal>
  );
}

/* ============ Modal thêm số lô (cmdthemsolo) ============ */
function AddSoLoModal({ company, onClose, onSaved }) {
  const [ma, setMa] = useState('');
  const [ten, setTen] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const save = async () => {
    if (!ma.trim()) { setErr('Mã số lô không được trống'); return; }
    setSaving(true); setErr('');
    try {
      await KskImportService.addSoLo(ma.trim(), ten.trim(), company.idKhachHang);
      onSaved(ma.trim());
    } catch (e) {
      setErr(e.response?.data?.message || 'Không lưu được số lô');
      setSaving(false);
    }
  };

  return (
    <Modal title={`Thêm số lô — ${company.tenKhachHang}`} onClose={onClose}>
      <Field label="Mã số lô *"><input style={mInput} value={ma} onChange={e => setMa(e.target.value)} autoFocus placeholder="VD: LO072026" /></Field>
      <Field label="Tên số lô"><input style={mInput} value={ten} onChange={e => setTen(e.target.value)} placeholder="VD: Đợt KSK tháng 7/2026" /></Field>
      {err && <div style={{ color: P.danger, fontSize: 13, marginTop: 8 }}>{err}</div>}
      <ModalActions onClose={onClose} onSave={save} saving={saving} />
    </Modal>
  );
}

/* ============ Modal cấu hình cột — web hóa frmExport_Excel_Struct ============
   export_excel_struct (row_begin) + export_excel_struct_detail
   (excel_column → sql_column, mô tả, tham chiếu mota_them, allow_blank, sort_order) */
function ColumnConfigModal({ onClose, onChanged }) {
  const [cfg, setCfg] = useState(null);
  const [rowBegin, setRowBegin] = useState(8);
  const [err, setErr] = useState('');
  const [savingId, setSavingId] = useState(null); // structDetailId đang lưu; -1 = dòng mới; 'master'; 'all'
  const [dirty, setDirty] = useState(false);        // đã ghi DB → đóng modal sẽ reload data file
  const [orderDirty, setOrderDirty] = useState(false); // đã kéo-thả nhưng CHƯA lưu
  const dragIdx = useRef(null);
  const emptyNew = { structDetailId: 0, sqlColumn: '', sqlColumnDescription: '', excelColumn: '', allowBlank: true, sortOrder: null, moTaThem: '' };
  const [newRow, setNewRow] = useState(emptyNew);

  const load = async () => {
    try {
      const c = await KskImportService.getColumns();
      setCfg(c);
      setRowBegin(c.rowBegin || 8);
      setErr('');
    } catch (e) {
      const st = e.response?.status;
      setErr(e.response?.data?.message
        || (st === 404 ? 'API /kskimport/columns chưa có (404) — backend chưa rebuild.' : `Lỗi tải cấu hình${st ? ` (HTTP ${st})` : ''}`));
      setCfg({ details: [] });
    }
  };
  useEffect(() => { load(); }, []);

  const patchDetail = (i, key, val) => {
    setCfg(p => {
      const details = [...p.details];
      details[i] = { ...details[i], [key]: val };
      return { ...p, details };
    });
  };

  const saveDetail = async (d, i) => {
    if (!d.sqlColumn.trim() || !d.excelColumn.trim()) { setErr('sql_column và cột Excel không được trống'); return; }
    setSavingId(d.structDetailId || -1); setErr('');
    try {
      const res = await KskImportService.saveColumnDetail(d);
      if (i === -1) setNewRow(emptyNew);
      await load();
      setDirty(true);
      if (res && i === -1) { /* dòng mới đã vào danh sách */ }
    } catch (e) { setErr(e.response?.data?.message || 'Không lưu được dòng cấu hình'); }
    finally { setSavingId(null); }
  };

  const removeDetail = async (d) => {
    if (!window.confirm(`Xóa cấu hình cột "${d.sqlColumn}" (Excel ${d.excelColumn})?`)) return;
    setSavingId(d.structDetailId); setErr('');
    try { await KskImportService.deleteColumnDetail(d.structDetailId); await load(); setDirty(true); }
    catch (e) { setErr(e.response?.data?.message || 'Không xóa được'); }
    finally { setSavingId(null); }
  };

  // Kéo-thả đổi thứ tự dòng → đánh lại sort_order = vị trí mới (chưa ghi DB)
  const handleDrop = (targetIdx) => {
    const from = dragIdx.current;
    dragIdx.current = null;
    if (from == null || from === targetIdx) return;
    setCfg(p => {
      const details = [...p.details];
      const [moved] = details.splice(from, 1);
      details.splice(targetIdx, 0, moved);
      details.forEach((d, i) => { d.sortOrder = i + 1; });
      return { ...p, details };
    });
    setOrderDirty(true);
  };

  // Lưu TẤT CẢ dòng (thứ tự sau kéo-thả + mọi ô đang sửa) — 1 transaction
  const saveAll = async () => {
    setSavingId('all'); setErr('');
    try {
      const details = cfg.details.map((d, i) => ({ ...d, sortOrder: i + 1 }));
      await KskImportService.saveColumnDetails(details);
      await load();
      setDirty(true);
      setOrderDirty(false);
    } catch (e) { setErr(e.response?.data?.message || 'Không lưu được cấu hình'); }
    finally { setSavingId(null); }
  };

  const saveMaster = async () => {
    setSavingId('master'); setErr('');
    try { await KskImportService.saveColumnMaster(parseInt(rowBegin, 10) || 8); await load(); setDirty(true); }
    catch (e) { setErr(e.response?.data?.message || 'Không lưu được row_begin'); }
    finally { setSavingId(null); }
  };

  const close = () => { if (dirty && onChanged) onChanged(); onClose(); };

  const cth = { padding: '8px 8px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: P.textDim, borderBottom: '2px solid ' + P.border, whiteSpace: 'nowrap', position: 'sticky', top: 0, background: P.inputBg, zIndex: 1 };
  const ctd = { padding: '5px 6px', borderBottom: '1px solid ' + P.border, verticalAlign: 'middle' };
  const cin = { width: '100%', boxSizing: 'border-box', padding: '6px 8px', borderRadius: 6, border: '1px solid ' + P.border, background: P.white, fontSize: 12.5, fontFamily: mono, color: P.text, outline: 'none' };
  const miniBtn = (bg, c) => ({ padding: '5px 10px', borderRadius: 6, border: 'none', background: bg, color: c, fontSize: 12, fontWeight: 600, fontFamily: font, cursor: 'pointer' });

  const DetailRow = ({ d, i, isNew }) => (
    <tr
      style={isNew ? { background: P.primaryLight } : { cursor: 'grab' }}
      draggable={!isNew}
      onDragStart={() => { dragIdx.current = i; }}
      onDragOver={e => { if (!isNew) e.preventDefault(); }}
      onDrop={() => { if (!isNew) handleDrop(i); }}
    >
      {/* Thứ tự + tay nắm kéo — đứng đầu */}
      <td style={{ ...ctd, width: 76, whiteSpace: 'nowrap' }}>
        {isNew ? (
          <span style={{ fontSize: 12, color: P.textMuted, paddingLeft: 6 }}>mới</span>
        ) : (
          <span title="Kéo để đổi thứ tự" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ color: P.textMuted, fontSize: 15, cursor: 'grab' }}>⠿</span>
            <span style={{ fontFamily: mono, fontWeight: 700, color: P.primary, minWidth: 24, textAlign: 'center' }}>{i + 1}</span>
          </span>
        )}
      </td>
      <td style={{ ...ctd, width: 70 }}>
        <input style={{ ...cin, textAlign: 'center', textTransform: 'uppercase' }} value={d.excelColumn}
          placeholder="A" maxLength={3}
          onChange={e => isNew ? setNewRow({ ...d, excelColumn: e.target.value }) : patchDetail(i, 'excelColumn', e.target.value)} />
      </td>
      <td style={{ ...ctd, minWidth: 170 }}>
        <input style={cin} value={d.sqlColumn} placeholder="sql_column"
          onChange={e => isNew ? setNewRow({ ...d, sqlColumn: e.target.value }) : patchDetail(i, 'sqlColumn', e.target.value)} />
      </td>
      <td style={{ ...ctd, minWidth: 190 }}>
        <input style={{ ...cin, fontFamily: font }} value={d.sqlColumnDescription} placeholder="Mô tả hiển thị"
          onChange={e => isNew ? setNewRow({ ...d, sqlColumnDescription: e.target.value }) : patchDetail(i, 'sqlColumnDescription', e.target.value)} />
      </td>
      <td style={{ ...ctd, minWidth: 150 }}>
        <input style={cin} value={d.moTaThem} placeholder="VD: 3.9-6.4"
          onChange={e => isNew ? setNewRow({ ...d, moTaThem: e.target.value }) : patchDetail(i, 'moTaThem', e.target.value)} />
      </td>
      <td style={{ ...ctd, textAlign: 'center', width: 70 }}>
        <input type="checkbox" checked={!!d.allowBlank}
          onChange={e => isNew ? setNewRow({ ...d, allowBlank: e.target.checked }) : patchDetail(i, 'allowBlank', e.target.checked)} />
      </td>
      <td style={{ ...ctd, whiteSpace: 'nowrap', width: 120 }}>
        <button style={miniBtn(P.primary, '#fff')} disabled={savingId != null}
          onClick={() => saveDetail(d, isNew ? -1 : i)}>
          {savingId === (d.structDetailId || -1) ? '...' : (isNew ? '＋ Thêm' : 'Lưu')}
        </button>
        {!isNew && (
          <button style={{ ...miniBtn('#fde8e8', P.danger), marginLeft: 6 }} disabled={savingId != null}
            onClick={() => removeDetail(d)}>Xóa</button>
        )}
      </td>
    </tr>
  );

  return (
    <Modal title="Cấu hình cột import — KETQUA_KSK (export_excel_struct)" onClose={close} wide>
      {cfg === null && <div style={{ textAlign: 'center', padding: 30 }}><Spinner size={30} /></div>}
      {err && <div style={{ color: P.danger, fontSize: 13, marginBottom: 10 }}>{err}</div>}

      {cfg !== null && (
        <>
          {/* Master: row_begin */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: P.textDim }}>Dữ liệu bắt đầu từ dòng Excel số:</span>
            <input style={{ ...cin, width: 70, textAlign: 'center', fontFamily: mono }} value={rowBegin}
              onChange={e => setRowBegin(e.target.value)} />
            <button style={miniBtn(P.primary, '#fff')} disabled={savingId != null} onClick={saveMaster}>
              {savingId === 'master' ? '...' : 'Lưu dòng bắt đầu'}
            </button>
            <span style={{ fontSize: 12, color: P.textMuted }}>
              {cfg.structId ? `struct_id: ${cfg.structId} • ${cfg.details.length} cột` : 'Chưa có cấu hình — thêm dòng đầu tiên sẽ tự tạo.'}
            </span>
          </div>

          <div style={{ overflow: 'auto', maxHeight: '52vh' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                <tr>
                  <th style={cth}>Thứ tự</th>
                  <th style={cth}>Cột Excel</th>
                  <th style={cth}>sql_column</th>
                  <th style={cth}>Mô tả</th>
                  <th style={cth}>Tham chiếu (mota_them)</th>
                  <th style={cth}>Cho trống</th>
                  <th style={cth}></th>
                </tr>
              </thead>
              <tbody>
                {/* Gọi DetailRow như hàm thường (không phải component) để tránh
                    re-mount làm mất focus input mỗi lần gõ phím */}
                {cfg.details.map((d, i) => <React.Fragment key={d.structDetailId}>{DetailRow({ d, i })}</React.Fragment>)}
                <React.Fragment key="new">{DetailRow({ d: newRow, i: -1, isNew: true })}</React.Fragment>
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: 12, fontSize: 12.5, color: P.textDim, lineHeight: 1.6 }}>
            Kéo-thả biểu tượng ⠿ để đổi thứ tự cột, sau đó bấm <strong>Lưu tất cả</strong>.
            Khi có cấu hình, việc đọc Excel chạy theo bảng này (cột Excel → sql_column, data từ dòng bắt đầu).
            Cột "Tham chiếu" dùng chung với màn kết quả xét nghiệm. Đóng modal sẽ tự load lại dữ liệu file theo cấu hình mới.
          </div>

          {/* Footer: Lưu tất cả + Đóng */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, marginTop: 16 }}>
            {orderDirty && (
              <span style={{ fontSize: 12.5, color: P.warning, marginRight: 'auto' }}>
                ⚠ Thứ tự đã thay đổi — chưa lưu
              </span>
            )}
            <button onClick={saveAll} disabled={savingId != null || cfg.details.length === 0} style={{
              padding: '9px 22px', borderRadius: 8, border: 'none',
              background: P.primary, color: '#fff', fontFamily: font, fontSize: 14, fontWeight: 600,
              cursor: savingId != null ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', gap: 8,
            }}>
              {savingId === 'all' && <Spinner size={14} color="#fff" />} 💾 Lưu tất cả
            </button>
            <button onClick={close} style={{
              padding: '9px 20px', borderRadius: 8, border: '1px solid ' + P.border,
              background: P.white, color: P.text, fontFamily: font, fontSize: 14, cursor: 'pointer',
            }}>Đóng</button>
          </div>
        </>
      )}
    </Modal>
  );
}

/* ============ UI helpers ============ */
function Modal({ title, children, onClose, wide }) {
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 999,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div onClick={e => e.stopPropagation()} className="app-modal" style={{
        background: P.white, borderRadius: 14, padding: '22px 26px',
        width: wide ? 'min(1100px, 96vw)' : 'min(460px, 96vw)',
        maxHeight: '88vh', overflowY: 'auto', fontFamily: font,
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: P.primary, marginBottom: 16 }}>{title}</div>
        {children}
      </div>
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: P.textDim, marginBottom: 6 }}>{label}</label>
      {children}
    </div>
  );
}
function ModalActions({ onClose, onSave, saving, closeOnly }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
      <button onClick={onClose} style={{
        padding: '9px 20px', borderRadius: 8, border: '1px solid ' + P.border,
        background: P.white, color: P.text, fontFamily: font, fontSize: 14, cursor: 'pointer',
      }}>{closeOnly ? 'Đóng' : 'Hủy'}</button>
      {!closeOnly && (
        <button onClick={onSave} disabled={saving} style={{
          padding: '9px 22px', borderRadius: 8, border: 'none',
          background: P.primary, color: '#fff', fontFamily: font, fontSize: 14, fontWeight: 600,
          cursor: saving ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', gap: 8,
        }}>{saving && <Spinner size={14} color="#fff" />} Lưu</button>
      )}
    </div>
  );
}
function Bdg({ bg, c, t }) {
  return <span style={{ padding: '2px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600, background: bg, color: c }}>{t}</span>;
}
const mInput = {
  width: '100%', boxSizing: 'border-box', padding: '10px 12px',
  borderRadius: 8, border: '1px solid ' + P.border, background: P.inputBg,
  fontSize: 14, fontFamily: font, color: P.text, outline: 'none',
};
const dropStyle = {
  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 30,
  background: P.white, border: '1px solid ' + P.border, borderRadius: 8,
  marginTop: 4, maxHeight: 240, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
};
const dropItem = { padding: '9px 14px', cursor: 'pointer', borderBottom: '1px solid ' + P.border };
const stepBadge = {
  width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
  background: P.primary, color: '#fff',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  fontSize: 14, fontWeight: 700, fontFamily: mono,
};
const btnStyle = (enabled, color) => ({
  padding: '10px 20px', borderRadius: 8, border: 'none',
  background: enabled ? color : '#b8cce0', color: '#fff',
  fontSize: 14, fontWeight: 600, fontFamily: font,
  cursor: enabled ? 'pointer' : 'not-allowed',
  display: 'flex', alignItems: 'center', gap: 8, height: 41,
});
